import { Machine, assign } from "xstate";
import { watchState } from "@/utils/machine-helpers";
import { apolloClient } from "@/lib/apollo/apollo";
import { Elements } from "@/graphql/queries";

const ELEMENT_PAGINATION_LIMIT = 20;

const context = {
  elements: [],
  elementGroup: "promo",
  elementTypeId: "",
  errors: [],
  fetchPolicy: "",
  paginationKey: "",
  selectedElementId: "",
};

const loadingOnDone = [
  {
    target: "#active.withElements.waiting",
    actions: ["updateElements", "updatePaginationKey"],
    cond: "canLoadMore",
  },
  {
    target: "#active.withElements.complete",
    actions: ["updateElements", "updatePaginationKey"],
    cond: "hasElements",
  },
  {
    target: "#active.empty",
  },
];

const loadingOnError = {
  target: "#active",
  actions: "updateErrors",
};

const invokeLoading = {
  src: (context) => loadElementsQuery(context),
  onDone: loadingOnDone,
  onError: loadingOnError,
};

export default Machine(
  {
    id: "elementsList",

    context,

    initial: "loading",

    states: {
      loading: {
        id: "loading",
        initial: "first",
        states: {
          first: {
            entry: "setFetchPolicy",
            invoke: {
              id: "loadFirstElements",
              ...invokeLoading,
            },
          },
          more: {
            entry: "setFetchPolicy",
            invoke: {
              id: "loadMoreElements",
              ...invokeLoading,
            },
          },
        },
      },
      active: {
        id: "active",
        on: {
          CREATE: "create",
          CHANGE_GROUP: {
            target: "loading.first",
            actions: [
              "assignElementGroup",
              "resetElements",
              "resetPaginationKey",
              "fixElementTypeId",
            ],
          },
          CHANGE_TYPE: {
            target: "loading.first",
            actions: [
              "assignElementType",
              "resetElements",
              "resetPaginationKey",
            ],
          },
        },
        states: {
          empty: {},
          withElements: {
            states: {
              waiting: {
                on: {
                  LOAD_MORE_ELEMENTS: {
                    target: "#loading.more",
                  },
                },
              },
              complete: {},
            },
          },
        },
      },
      create: {
        entry: [
          assign({
            elementGroup: "custom",
          }),
        ],
        on: {
          CLOSE_CREATE: {
            target: "loading.first",
            actions: [
              assign({
                elementGroup: "custom",
              }),
              "resetElements",
              "resetPaginationKey",
              "fixElementTypeId",
            ],
          },
          ELEMENT_ADDED: {
            target: "loading.first",
            actions: [
              assign({
                elementGroup: "custom",
              }),
              "resetElements",
              "resetPaginationKey",
              "fixElementTypeId",
            ],
          },
        },
      },
    },
  },
  {
    actions: {
      assignElementGroup: assign({
        elementGroup: (context, event) => event.elementGroup,
      }),
      assignElementType: assign({
        elementTypeId: (context, event) => event.elementTypeId,
      }),
      fixElementTypeId: assign({
        elementTypeId: (context, event) => {
          let elTypeId = context.elementTypeId;
          const elementGroup = event.elementGroup || context.elementGroup;
          // If the elementTypeId is either banner-image or background-photo and the elementGroup is custom we need to set the default elementTypeId to background-photo
          if (
            elementGroup === "custom" &&
            elTypeId !== "background-photo" &&
            elTypeId !== "banner-image"
          ) {
            elTypeId = "background-photo";
          }
          return elTypeId;
        },
      }),
      resetElements: assign({
        elements: [],
      }),
      resetPaginationKey: assign({
        paginationKey: "",
      }),
      setFetchPolicy: assign({
        fetchPolicy: (context, event) =>
          context.elementGroup === "custom" ? "network-only" : "cache-first",
      }),
      updateErrors: assign({
        errors: (context, event) => {
          return mapGraphQlErrorResponse(event?.data?.graphQLErrors);
        },
      }),
      updateElements: assign({
        // Merge the existing elements with the new elements...
        elements: (context, event) => [
          ...context.elements,
          ...event.data?.data?.elements?.elements,
        ],
      }),
      updatePaginationKey: assign({
        paginationKey: (context, event) =>
          event.data?.data?.elements?.paginationKey,
      }),
    },
    guards: {
      canLoadMore: (context, event) => {
        const { paginationKey } = event.data?.data?.elements;

        return elementsExist(event) && paginationKey ? true : false;
      },
      hasElements: (context, event) => {
        return elementsExist(event) ? true : false;
      },
    },
  }
);

const loadElementsQuery = (context) => {
  const { elementGroup, elementTypeId, fetchPolicy, paginationKey } = context;
  const customElementsOnly = elementGroup === "custom";

  const variables = {
    input: {
      customElementsOnly,
      elementTypeId,
      limit: ELEMENT_PAGINATION_LIMIT,
      paginationKey,
    },
  };

  return apolloClient.query({
    query: Elements,
    variables,
    fetchPolicy,
  });
};

const elementsExist = (event) => {
  const elements = event.data?.data?.elements?.elements ?? [];
  return elements.length;
};
